import React, { useEffect, useState } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/TestAuthContext';
import axios from '@/lib/http';
import NarrativeEditor from '../components/wizard/NarrativeEditor';
import AppLayout from '../components/AppLayout';
import AccountantLayout from '../components/AccountantLayout';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Narrator Page - Standalone AI Report Narrator
 * Accessible from report detail page (Clerk auth) or accountant portal (accountant auth)
 * Loads existing narrative if available
 */
export default function NarratorPage() {
  const { id, reportId } = useParams();
  const location = useLocation();
  const { getToken } = useAuth();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Check if this is accountant route
  const isAccountantRoute = location.pathname.startsWith('/accountant/');
  const actualReportId = reportId || id;

  useEffect(() => {
    const fetchReport = async () => {
      try {
        let headers = {};
        
        if (isAccountantRoute) {
          // Use accountant token from localStorage
          const accountantToken = localStorage.getItem('accountant_token');
          if (accountantToken) {
            headers = { Authorization: `Bearer ${accountantToken}` };
          }
          
          // Fetch from collaboration endpoint
          const response = await axios.get(
            `${BACKEND_URL}/api/v1/collaboration/reports/${actualReportId}/view`,
            { headers }
          );
          setReport(response.data);
        } else {
          // Use Clerk token
          const token = await getToken();
          headers = { Authorization: `Bearer ${token}` };
          
          // Fetch from regular endpoint
          const response = await axios.get(
            `${BACKEND_URL}/api/v1/reports/${actualReportId}`,
            { headers }
          );
          setReport(response.data);
        }
        
        setLoading(false);
      } catch (err) {
        console.error('Failed to fetch report:', err);
        setLoading(false);
      }
    };

    if (actualReportId) {
      fetchReport();
    }
  }, [actualReportId, getToken, isAccountantRoute]);

  // F2.67: every entry into NarratorPage gets navigation chrome so the
  // user isn't orphaned. Accountant-portal route uses the JWT-gated
  // AccountantLayout; Clerk in-house route uses the standard AppLayout.
  const wrap = (node: React.ReactElement) =>
    isAccountantRoute ? <AccountantLayout>{node}</AccountantLayout> : <AppLayout>{node}</AppLayout>;

  if (loading) {
    return wrap(
      <div
        className="flex items-center justify-center min-h-screen"
        style={{ backgroundColor: 'var(--color-bg-base)' }}
      >
        <div
          className="animate-spin rounded-full h-12 w-12"
          style={{ borderBottom: '2px solid var(--color-brand)' }}
        ></div>
      </div>
    );
  }

  if (!report) {
    return wrap(
      <div
        className="flex items-center justify-center min-h-screen"
        style={{ backgroundColor: 'var(--color-bg-base)' }}
      >
        <div className="text-center">
          <h2 className="cs-h2">Report not found</h2>
          <p className="cs-meta mt-2">Unable to load claim data</p>
        </div>
      </div>
    );
  }

  return wrap(
    <NarrativeEditor
      claimId={actualReportId || ''}
      claimData={{
        client_name: report.client_name,
        claimant_name: report.claimant_name,
        business_type: report.business_type || 'Unknown',
        zone: report.zone || 'Unknown'
      }}
      calculationResults={{
        ...(report.result || {}),
        sensitivity_analysis: report.sensitivity_analysis,
        monte_carlo_analysis: report.monte_carlo_analysis
      }}
      eventType={report.event_type || 'Hurricane'}
      existingNarrative={report.narrative || null}
    />
  );
}
